/**
 * Shopping Lists API — typed wrappers around `/api/shoppinglists/*`.
 *
 * Ported from legacy `frontend/src/services/shoppingListsService.ts`. Display
 * helper functions (getItemDisplayTitle, groupItemsByStore, etc.) are NOT
 * ported here — those are UI concerns and belong in apps/web (or a future
 * shared UI selector module if they prove reusable).
 */

import type { HttpClient } from './client';
import type {
  AddShoppingListItemRequest,
  CreateShoppingListRequest,
  UpdateShoppingListItemRequest,
  UpdateShoppingListRequest,
} from '../types/requests';
import {
  type ActiveShoppingListResponse,
  type AddShoppingListItemResponse,
  type BatchPurchaseResponse,
  type ShoppingList,
  type ShoppingListItem,
  type ShoppingListWithItemsResponse,
  type ShoppingListsResponse,
  activeShoppingListResponseSchema,
  addShoppingListItemResponseSchema,
  batchPurchaseResponseSchema,
  shoppingListItemSchema,
  shoppingListSchema,
  shoppingListWithItemsResponseSchema,
  shoppingListsResponseSchema,
} from '../types/shopping-list';

const API_BASE = '/api/shoppinglists';

export class ShoppingListsApi {
  constructor(private readonly http: HttpClient) {}

  // -------------------------------------------------------------------------
  // Lists
  // -------------------------------------------------------------------------

  fetchAll(): Promise<ShoppingListsResponse> {
    return this.http.get(API_BASE, shoppingListsResponseSchema);
  }

  fetchActive(): Promise<ActiveShoppingListResponse> {
    return this.http.get(
      `${API_BASE}/active?include_checked=true`,
      activeShoppingListResponseSchema
    );
  }

  fetch(listId: string): Promise<ShoppingListWithItemsResponse> {
    return this.http.get(
      `${API_BASE}/${encodeURIComponent(listId)}`,
      shoppingListWithItemsResponseSchema
    );
  }

  create(input: CreateShoppingListRequest): Promise<ShoppingList> {
    return this.http.post(API_BASE, input, shoppingListSchema);
  }

  update(listId: string, input: UpdateShoppingListRequest): Promise<ShoppingList> {
    return this.http.patch(
      `${API_BASE}/${encodeURIComponent(listId)}`,
      input,
      shoppingListSchema
    );
  }

  delete(listId: string): Promise<void> {
    return this.http.delete(`${API_BASE}/${encodeURIComponent(listId)}`);
  }

  archive(listId: string): Promise<ShoppingList> {
    return this.update(listId, { status: 'ARCHIVED' });
  }

  // -------------------------------------------------------------------------
  // Items
  // -------------------------------------------------------------------------

  // BUG6: backend returns the base `ShoppingListItem` shape from POST
  // /items (consistent with update/check/purchase). Validating against
  // `shoppingListItemWithDetailsSchema` here would throw on the missing
  // join fields (`list_name`, `household_id`, `display_title`). Callers
  // that need WithDetails must reconcile the server response with their
  // optimistic row (the orchestrator does this in `handleAddItem`).
  // T12.1.8.4 — an offer-only add can return the "would create — confirm?"
  // signal instead of the created item, so validate against the response union
  // (`addShoppingListItemResponseSchema`). Callers discriminate on
  // `'needsConfirmation' in result`.
  addItem(
    listId: string,
    input: AddShoppingListItemRequest
  ): Promise<AddShoppingListItemResponse> {
    return this.http.post(
      `${API_BASE}/${encodeURIComponent(listId)}/items`,
      input,
      addShoppingListItemResponseSchema
    );
  }

  addToActive(
    input: AddShoppingListItemRequest
  ): Promise<AddShoppingListItemResponse> {
    return this.http.post(
      `${API_BASE}/active/items`,
      input,
      addShoppingListItemResponseSchema
    );
  }

  updateItem(
    listId: string,
    itemId: string,
    input: UpdateShoppingListItemRequest
  ): Promise<ShoppingListItem> {
    return this.http.patch(
      `${API_BASE}/${encodeURIComponent(listId)}/items/${encodeURIComponent(itemId)}`,
      input,
      shoppingListItemSchema
    );
  }

  deleteItem(listId: string, itemId: string): Promise<void> {
    return this.http.delete(
      `${API_BASE}/${encodeURIComponent(listId)}/items/${encodeURIComponent(itemId)}`
    );
  }

  checkItem(
    listId: string,
    itemId: string,
    checked: boolean
  ): Promise<ShoppingListItem> {
    return this.http.post(
      `${API_BASE}/${encodeURIComponent(listId)}/items/${encodeURIComponent(itemId)}/check`,
      { checked },
      shoppingListItemSchema
    );
  }

  purchaseItem(
    listId: string,
    itemId: string,
    actualPrice?: number
  ): Promise<ShoppingListItem> {
    return this.http.post(
      `${API_BASE}/${encodeURIComponent(listId)}/items/${encodeURIComponent(itemId)}/purchase`,
      { actualPrice },
      shoppingListItemSchema
    );
  }

  batchPurchase(listId: string, itemIds: string[]): Promise<BatchPurchaseResponse> {
    return this.http.post(
      `${API_BASE}/${encodeURIComponent(listId)}/purchase`,
      { itemIds },
      batchPurchaseResponseSchema
    );
  }
}
